import { importLibrary } from '@googlemaps/js-api-loader';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Address } from '../models/Address';
import { buildStreet, findAddressPart, getAddressFromGeoCode } from '../utils/geocodeAddress';

type ContextValue = {
  autocompleteService: typeof google.maps.places.AutocompleteSuggestion | null;
  MarkerElement: typeof google.maps.marker.AdvancedMarkerElement | null;
  geoCoder: google.maps.Geocoder | null;
  Map: typeof google.maps.Map | null;
};

const defaultContextValue: ContextValue = {
  autocompleteService: null,
  MarkerElement: null,
  geoCoder: null,
  Map: null,
};

type Context = {
  setContext: React.Dispatch<React.SetStateAction<ContextValue>>;
  context: ContextValue;
};

export const GoogleMapsContext = createContext<Context>({
  setContext: () => {},
  context: defaultContextValue,
});

export const GoogleMapsProvider = ({ children }: React.PropsWithChildren) => {
  const [context, setContext] = useState<ContextValue>(defaultContextValue);
  const contextValue = useMemo(() => ({ context, setContext }), [context]);

  useEffect(() => {
    importLibrary('places').then(({ AutocompleteSuggestion }) => {
      setContext((prev) => ({ ...prev, autocompleteService: AutocompleteSuggestion }));
    });

    importLibrary('marker').then(({ AdvancedMarkerElement }) => {
      setContext((prev) => ({ ...prev, MarkerElement: AdvancedMarkerElement }));
    });

    importLibrary('geocoding').then(({ Geocoder }) => {
      setContext((prev) => ({ ...prev, geoCoder: new Geocoder() }));
    });

    importLibrary('maps').then(({ Map }) => {
      setContext((prev) => ({ ...prev, Map: Map }));
    });
  }, []);

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      <>{children}</>
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => {
  const { context } = useContext(GoogleMapsContext);
  const staticMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }

  async function getQueryPredictions(
    input: string
  ): Promise<google.maps.places.AutocompleteSuggestion[]> {
    if (!context?.autocompleteService) {
      throw new Error('Autocomplete service not found');
    }

    const suggestions = await context.autocompleteService.fetchAutocompleteSuggestions({
      input,
      includedRegionCodes: ['SE'],
      // "premise" is what rural addresses without a street are, e.g. "Maglasäte
      // 311". Without it the only match is the nearby road, positioned at the
      // middle of the road rather than at the house.
      includedPrimaryTypes: ['street_address', 'route', 'premise'],
    });

    return suggestions.suggestions || [];
  }

  async function getAddress(placeId: string): Promise<Address | undefined> {
    const place = new google.maps.places.Place({ id: placeId });

    const fields = await place.fetchFields({
      fields: ['addressComponents', 'location'],
    });

    if (!fields || !fields.place || !fields.place.location) {
      return undefined;
    }

    const parts = (fields.place.addressComponents ?? []).map((component) => ({
      types: component.types,
      name: component.longText ?? '',
    }));

    let streetAddress = buildStreet(parts);
    let zipCode = findAddressPart(parts, 'postal_code');
    let city = findAddressPart(parts, 'postal_town') || findAddressPart(parts, 'locality');

    if ((!zipCode || !city) && context?.geoCoder) {
      const geoResponse = await context.geoCoder.geocode({ location: fields.place.location });
      const geocodedAddress = getAddressFromGeoCode(geoResponse.results);

      zipCode = (zipCode || geocodedAddress?.zipCode) ?? '';
      city = (city || geocodedAddress?.city) ?? '';

      if (!streetAddress && geocodedAddress?.street) {
        streetAddress = geocodedAddress.street;
      }
    }

    return {
      latitude: fields.place.location.lat(),
      longitude: fields.place.location.lng(),
      street: streetAddress,
      zipCode,
      city,
    };
  }

  async function getAddressFromLatLng(latLng: google.maps.LatLng): Promise<Address | undefined> {
    const geoCoder = context?.geoCoder;
    if (!geoCoder) {
      throw new Error('Geocoder not found');
    }
    const response = await geoCoder.geocode({ location: latLng });
    const address = getAddressFromGeoCode(response.results);

    if (!address) {
      return undefined;
    }

    // Keep the point the user picked: the geocoded position can be the nearest
    // address some way off, and the pin would jump there when reopened.
    return { ...address, latitude: latLng.lat(), longitude: latLng.lng() };
  }

  async function getAddressFromStreet(street: string): Promise<Address | undefined> {
    const geoCoder = context?.geoCoder;
    if (!geoCoder) {
      throw new Error('Geocoder not found');
    }
    const response = await geoCoder.geocode({ address: street });

    return getAddressFromGeoCode(response.results);
  }

  function createMarker(
    position: google.maps.LatLng | google.maps.LatLngLiteral,
    title?: string,
    map?: google.maps.Map
  ) {
    if (!context?.MarkerElement) {
      throw new Error('MarkerElement not found');
    }
    return new context.MarkerElement({ position, map, title });
  }

  function setMarker(
    position: google.maps.LatLng | google.maps.LatLngLiteral,
    map?: google.maps.Map
  ) {
    if (!context?.MarkerElement) {
      throw new Error('MarkerElement not found');
    }

    if (staticMarkerRef.current) {
      staticMarkerRef.current.remove();
    }

    const newMarker = new context.MarkerElement({ position, map });
    staticMarkerRef.current = newMarker;
    return newMarker;
  }

  function createMap(element: HTMLDivElement, options?: google.maps.MapOptions) {
    if (!context?.Map) {
      throw new Error('Map not found');
    }

    return new context.Map(element, options);
  }

  async function getTransportTime(
    origin: Address,
    destination: Address
  ): Promise<number | undefined> {
    try {
      const { RouteMatrix, RoutingPreference, TravelMode } = await importLibrary('routes');

      const { matrix } = await RouteMatrix.computeRouteMatrix({
        origins: [{ lat: origin.latitude, lng: origin.longitude }],
        destinations: [{ lat: destination.latitude, lng: destination.longitude }],
        travelMode: TravelMode.DRIVING,
        routingPreference: RoutingPreference.TRAFFIC_UNAWARE,
        fields: ['durationMillis', 'condition'],
      });

      const item = matrix.rows[0]?.items[0];
      if (!item || item.condition !== 'ROUTE_EXISTS') return undefined;

      // Rounded, not truncated: the backend rounds the same duration when it builds
      // the optimizer's distance matrix, and a task whose transport time disagrees
      // with the matrix by even a minute is rejected under Transportstart.
      return Math.round((item.durationMillis ?? 0) / 60000);
    } catch {
      return undefined;
    }
  }

  return {
    getQueryPredictions,
    getAddress,
    createMarker,
    getAddressFromLatLng,
    createMap,
    setMarker,
    getAddressFromStreet,
    getTransportTime,
  };
};
